import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "file-manager-wrapper" }
const _hoisted_2 = { class: "project-design-section" }
const _hoisted_3 = {
  key: 1,
  class: "embedded-header"
}
const _hoisted_4 = { class: "header-actions" }
const _hoisted_5 = {
  key: 2,
  class: "project-context"
}
const _hoisted_6 = { class: "project-context-content" }
const _hoisted_7 = { class: "project-info" }
const _hoisted_8 = {
  key: 0,
  class: "loading-indicator"
}
const _hoisted_9 = {
  key: 1,
  class: "error-message"
}
const _hoisted_10 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_11 = {
  key: 3,
  class: "files-container"
}
const _hoisted_12 = { class: "grid-wrapper" }

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from '@/composables/useStore';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import DesignService from '@/services/design.service';
import type { UploadedFile } from '@/types';

import AppCard from '../../base/AppCard.vue';
import AppButton from '../../base/AppButton.vue';
import AppPageHeader from '../../base/AppPageHeader.vue';
import AppDataGrid from '../../base/AppDataGrid.vue';
import DeleteConfirmationModal from '../documents/DeleteConfirmationModal.vue';
import NewDesignModal from './NewDesignModal.vue';

// Define props for when used as a component in DynamicContent
interface Props {
  projectId?: string | number | null;
}

interface Emits {
  (e: 'design-selected', design: UploadedFile): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectDesign',
  props: {
    projectId: { default: null }
  },
  emits: ["design-selected"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

// Emits
const emit = __emit;

const store = useStore();
const { t } = useI18n();
const router = useRouter();

// Data
const files = ref<any[]>([]);
const showDeleteConfirmation = ref(false);
const fileToDelete = ref<any>(null);
const sourceProject = ref<any>(null);
const gridApi = ref<any>(null);
const gridColumnApi = ref<any>(null);
const error = ref<string | null>(null);
const loading = ref(false);
const isInitialLoad = ref(true);
const selectedDesignType = ref('all');
const showNewDesignModal = ref(false);

const filteredFiles = computed(() => {
  let result = files.value;
  
  // Filter by design type
  if (selectedDesignType.value !== 'all') {
    result = result.filter((file: any) => file.designType === selectedDesignType.value);
  }
  
  return result;
});

const getFileIcon = (filename: string) => {
  if (!filename) return '';

  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, string> = {
    'pdf': '<i class="fas fa-file-pdf" style="color: #e74c3c;"></i>',
    'doc': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'docx': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'dwg': '<i class="fas fa-drafting-compass" style="color: #27ae60;"></i>',
    'dxf': '<i class="fas fa-drafting-compass" style="color: #27ae60;"></i>',
    'jpg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'jpeg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'png': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'gif': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'bmp': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'tiff': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'zip': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
    'rar': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
  };

  return extension ? (iconMap[extension] || '<i class="fas fa-file" style="color: #95a5a6;"></i>') : '';
};

// Grid Configuration - make reactive to language changes
const columnDefs = computed(() => [
  {
    headerName: '#',
    field: 'rowNumber',
    valueGetter: (params: any) => params.node.rowIndex + 1,
    width: 70,
    pinned: 'left',
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    field: 'fileType',
    headerName: '',
    width: 60,
    cellRenderer: (params: any) => {
      const span = document.createElement('span');
      span.innerHTML = getFileIcon(params.data.fileName);
      return span;
    },
    resizable: false,
    sortable: false,
    filter: false
  },
  {
    field: 'fileName',
    headerName: t('design.filename'),
    type: 'text',
    sortable: true,
    resizable: true,
    flex: 1,
    minWidth: 150
  },
  {
    field: 'designType',
    headerName: t('design.designType'),
    type: 'text',
    sortable: true,
    resizable: true,
    minWidth: 100
  },
  {
    field: 'createdAt',
    headerName: t('design.uploadDate'),
    type: 'date',
    sortable: true,
    resizable: true,
    minWidth: 180
  }
]);

// Actions configuration for AppDataGrid (dynamic based on file type)
const actions = computed(() => [
  {
    name: 'open',
    icon: 'fas fa-external-link-alt',
    label: t('design.openInDesigner'),
    tooltip: t('design.openInDesigner'),
    class: 'open-btn',
    // Dynamic action based on file type
    getLabel: (data: any) => {
      const fileName = data.fileName || '';
      const isDesignFile = fileName.toLowerCase().endsWith('.sh3d') || fileName.toLowerCase().endsWith('.sh3x');
      return isDesignFile ? t('design.openInDesigner') : t('design.download');
    },
    getIcon: (data: any) => {
      const fileName = data.fileName || '';
      const isDesignFile = fileName.toLowerCase().endsWith('.sh3d') || fileName.toLowerCase().endsWith('.sh3x');
      return isDesignFile ? 'fas fa-external-link-alt' : 'fas fa-download';
    }
  },
  ({
    name: 'delete',
    icon: 'fas fa-trash',
    label: t('design.delete'),
    tooltip: t('design.delete'),
    class: 'delete-btn',
    // Only allow delete when the creator (createdBy) matches the current user
    condition: (data: any) => {
      try {
        const user = store.getters.getUser.value;
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  } as any)
]);

const defaultColDef = {
  resizable: true,
  wrapText: false,
  autoHeight: false,
};

// Fetch files
const fetchFiles = async (force = false) => {
  if (!props.projectId) {
    error.value = t('notifications.selectProjectFirst');
    files.value = [];
    return;
  }

  // Prevent double loading unless forced
  if (loading.value || (!force && !isInitialLoad.value)) {
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await DesignService.getProjectDesignFiles(props.projectId);
    files.value = response || [];
    isInitialLoad.value = false;
  } catch (err: any) {
    console.error('Error fetching design files:', err);
    error.value = err.message || t('errors.loadingFilesError');
  } finally {
    loading.value = false;
  }
};

// Grid event handlers
const onGridReady = (params: any) => {
  gridApi.value = params.api;
  gridColumnApi.value = params.columnApi;

  // Auto-size all columns to fit the content
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};

// File deletion handling
const openFile = async (file: any) => {
  if (!props.projectId || !file?.id) {
    error.value = t('errors.invalidFileDownload');
    return;
  }

  try {
    loading.value = true;
    
    // Download the file securely and get a blob URL
    const blobUrl = await DesignService.downloadDesignFile(props.projectId, file.id);
    
    // Store the blob URL securely in session storage (it's temporary anyway)
    sessionStorage.setItem('designFileUrl', blobUrl);
    
    // Navigate to SweetHome3D with secure path parameters
    router.push({
      name: 'sweethome3d',
      params: {
        projectId: props.projectId,
        designId: file.id
      }
    });
  } catch (err: any) {
    console.error('Error opening design file:', err);
    error.value = err.message || t('errors.downloadingFileError');
  } finally {
    loading.value = false;
  }
};

const promptDeleteFile = (file: any) => {
  if (loading.value) return; // Prevent opening delete modal while loading
  fileToDelete.value = file;
  showDeleteConfirmation.value = true;
};

const isDeleting = ref(false);

const confirmDelete = async () => {
  if (!fileToDelete.value || !props.projectId || isDeleting.value) return;

  try {
    isDeleting.value = true;
    loading.value = true;
    await DesignService.deleteProjectDesignFile(String(props.projectId), fileToDelete.value.id);
    showDeleteConfirmation.value = false;
    // Show success notification
    store.dispatch('notifications/showSuccess', t('notifications.fileDeleteSuccess'));    
    // Clear deleted file reference
    fileToDelete.value = null;
    // Refresh file list after deletion
    await fetchFiles(true);
  } catch (err: any) {
    console.error('Error deleting design file:', err);
    error.value = err.message || t('errors.deletingFileError');
    store.dispatch('notifications/showError', error.value);
  } finally {
    loading.value = false;
    isDeleting.value = false;
  }
};

// Cancel delete handler
const cancelDelete = () => {
  showDeleteConfirmation.value = false;
  fileToDelete.value = null;
};

// New design handlers
const handleNewDesignClick = () => {
  if (!props.projectId) {
    error.value = t('notifications.selectFirmFirst');
    return;
  }
  showNewDesignModal.value = true;
};

const handleDesignCreated = async () => {
  showNewDesignModal.value = false;
  // Show success notification
  store.dispatch('notifications/showSuccess', t('design.createSuccess'));
  // Refresh file list after creation
  await fetchFiles(true);
};

const handleNewDesignModalClose = () => {
  showNewDesignModal.value = false;
};

// Handle action clicks from AppDataGrid
const handleAction = (action: any, data: any) => {
  if (action.name === 'open') {
    openFile(data);
  } else if (action.name === 'delete') {
    promptDeleteFile(data);
  }
};

// Window resize handler
const onWindowResize = () => {
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};

// Define emits for parent components to listen to
// const emit = defineEmits(['return-to-project']); // Removed duplicate

// Return to the project details page
const returnToProject = () => {
  // If this component is used inside a project details page, emit an event to parent
  // that will be handled by the ProjectDetails.vue's returnToProject method
  if (props.projectId) {
    emit('return-to-project');
    return;
  }

  // Otherwise navigate back to project (if coming from route)
  if (sourceProject.value && sourceProject.value.id) {
    router.push({
      path: '/project/' + sourceProject.value.id
    });
  } else {
    // Fallback to projects listing if something went wrong
    router.push('/projects');
  }
};

// Component lifecycle hooks
onMounted(async () => {
  // Load files when component mounts if we have a projectId
  if (props.projectId) {
    await fetchFiles();
  } else {
    // show a helpful message when no project is selected
    error.value = t('notifications.selectFirmFirst');
  }

  // Add resize event listener for the grid
  window.addEventListener('resize', onWindowResize);
});

onUnmounted(() => {
  // Remove resize event listener when component is unmounted
  window.removeEventListener('resize', onWindowResize);
});

// Watch for projectId changes
watch(() => props.projectId, async (newId: string | number | null) => {
  if (newId) {
    isInitialLoad.value = true;
    await fetchFiles();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (!props.projectId)
        ? (_openBlock(), _createBlock(AppPageHeader, {
            key: 0,
            title: _ctx.$t('design.title'),
            subtitle: _ctx.$t('design.subtitle')
          }, {
            actions: _withCtx(() => [
              _createVNode(AppButton, {
                variant: "primary",
                size: "medium",
                onClick: handleNewDesignClick,
                disabled: loading.value
              }, {
                default: _withCtx(() => [
                  _cache[0] || (_cache[0] = _createElementVNode("span", { class: "btn-icon" }, "+", -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('design.newDesign')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }, 8, ["title", "subtitle"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[2] || (_cache[2] = _createElementVNode("h2", { class: "component-title" }, null, -1)),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(AppButton, {
                variant: "primary",
                size: "medium",
                onClick: handleNewDesignClick,
                disabled: loading.value
              }, {
                default: _withCtx(() => [
                  _cache[1] || (_cache[1] = _createElementVNode("span", { class: "btn-icon" }, "+", -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('design.newDesign')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])
          ])),
      (sourceProject.value && !props.projectId)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("h3", null, _toDisplayString(_ctx.$t('design.projectFiles', { projectName: sourceProject.value.name })), 1),
                _createElementVNode("p", null, _toDisplayString(_ctx.$t('design.projectFilesDescription')), 1)
              ]),
              _createVNode(AppButton, {
                variant: "outline",
                size: "small",
                onClick: returnToProject,
                class: "return-to-project-btn"
              }, {
                default: _withCtx(() => [
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "btn-icon" }, [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "16",
                      height: "16"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"
                      })
                    ])
                  ], -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('design.returnToProject')), 1)
                ]),
                _: 1
              })
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(AppCard, { class: "mt-lg" }, {
        default: _withCtx(() => [
          (loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "spinner" }, null, -1)),
                _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
              ]))
            : (error.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("p", null, _toDisplayString(error.value), 1),
                  _createVNode(AppButton, {
                    onClick: fetchFiles,
                    variant: "outline",
                    size: "small"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.retry')), 1)
                    ]),
                    _: 1
                  })
                ]))
              : (files.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _cache[5] || (_cache[5] = _createElementVNode("i", {
                      class: "pi pi-file",
                      style: {"font-size":"3rem"}
                    }, null, -1)),
                    _createElementVNode("p", null, _toDisplayString(_ctx.$t('design.noFiles')), 1)
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createVNode(AppDataGrid, {
                        columnDefs: columnDefs.value,
                        rowData: filteredFiles.value,
                        defaultColDef: defaultColDef,
                        pagination: true,
                        pageSize: 20,
                        pageSizeOptions: [5, 10, 25, 50, 100],
                        suppressPaginationPanel: false,
                        animateRows: true,
                        showSearch: true,
                        searchPlaceholder: _ctx.$t('design.searchFiles'),
                        onGridReady: onGridReady,
                        onActionClicked: handleAction,
                        suppressMovableColumns: false,
                        gridStyle: "width: 100%;",
                        domLayout: "autoHeight",
                        actions: actions.value
                      }, null, 8, ["columnDefs", "rowData", "searchPlaceholder", "actions"])
                    ])
                  ]))
        ]),
        _: 1
      }),
      _createVNode(_Transition, { name: "fade" }, {
        default: _withCtx(() => [
          (showDeleteConfirmation.value)
            ? (_openBlock(), _createBlock(DeleteConfirmationModal, {
                key: 0,
                filename: fileToDelete.value?.fileName || '',
                "doc-type": fileToDelete.value?.designType || '',
                description: fileToDelete.value?.description || '',
                loading: loading.value,
                onConfirm: confirmDelete,
                onCancel: cancelDelete
              }, null, 8, ["filename", "doc-type", "description", "loading"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(NewDesignModal, {
        show: showNewDesignModal.value,
        "project-id": props.projectId,
        onCreated: handleDesignCreated,
        onClose: handleNewDesignModalClose
      }, null, 8, ["show", "project-id"])
    ])
  ]))
}
}

})